import type { ResultOf } from 'gql.tada'
import { useQuery } from '@tanstack/vue-query'
import { queryKeys } from '~/queryClient'
import { execute, gql } from '~/services-v2/financial-bff/financial-bff-gql'

const GQL = gql(`
  query Projects_partner_overview($partnerId: String!) {
    projects_partner_overview(partner_id: $partnerId) {
      all {
        total
        in_progress
        in_reassassement
        finish
        canceled
        expired
      }
      simulation {
        total
        approved
        reassassement
        canceled
        expired
      }
      proposal {
        total
        in_equipments
        in_customer_data
        in_biometry_and_contract
        in_payment_model
        canceled
        expired
      }
      installation {
        total
        in_progress
        canceled
        expired
      }
      status
      last_updated_at
      next_update_at
    }
  }
`)

export type ProjectsPartnerOverviewQuery = ResultOf<typeof GQL>
export type ProjectsPartnerOverviewData = ProjectsPartnerOverviewQuery['projects_partner_overview']

export default function useProjectsPartnerOverview(partnerId: Ref<string | undefined | null>) {
  const queryKey = queryKeys.PROJECTS_PARTNER_OVERVIEW(partnerId)

  const queryFn = async () => {
    if (!partnerId.value) {
      throw new Error('Partner ID is required')
    }

    const res = await execute(GQL, {
      partnerId: partnerId.value,
    })

    if (!res.projects_partner_overview) {
      throw new Error('Failed to fetch projects partner overview')
    }

    return res.projects_partner_overview
  }

  const query = useQuery({
    queryKey,
    queryFn,
    enabled: !!partnerId.value,
    retry: 3,
    refetchInterval: (query) => {
      const data = query.state.data as ProjectsPartnerOverviewData | undefined
      if (data?.status === 'PROCESSING') {
        return 1000 * 60
      }
      return false
    },
  })

  return query
}

export type UseProjectsPartnerOverview = ReturnType<typeof useProjectsPartnerOverview>
export type UseProjectsPartnerOverviewData = UseProjectsPartnerOverview['data']['value']
